import { sumBy } from "lodash";

import { components } from "@/@types/api";
import { Layer } from "@/Utils/hierarchy";

import { calculateROI } from "./spend-revenue-summary";

type Revenue = components["schemas"]["ScenarioResultRevenue"];
type Spend = components["schemas"]["ScenarioResultSpend"];

export interface FunnelStageData {
  stage: string;
  spend: number;
  revenue: number;
  roi: number | undefined;
  spendPercentage: number;
  revenuePercentage: number;
}

const FUNNEL_STAGE_LABELS = {
  [Layer.Brand]: "Brand Marketing",
  [Layer.Category]: "Category Marketing",
  [Layer.Product]: "Product Marketing",
} as const;

// Determines the layer of a spend/revenue record using the same logic as SpendBreakdown.tsx
const getRecordLayer = (record: Spend | Revenue): Layer => {
  const brand = record.marketingSpendBrand || "default";
  const category = record.marketingSpendCategory || "default"; // Maps to product_line
  const product = record.marketingSpendProduct || "default";

  // Use the same hierarchy logic as determineNodeLayer
  if (product !== "default") {
    return Layer.Product;
  } else if (category !== "default") {
    return Layer.Category;
  } else if (brand !== "default") {
    return Layer.Brand;
  } else {
    return Layer.Portfolio;
  }
};

export const getFunnelStageBreakdown = (
  revenues: Revenue[],
  spends: Spend[]
): FunnelStageData[] => {
  const totalSpend = sumBy(spends, "spend");
  const totalRevenue = sumBy(revenues, "revenue");

  // Aggregate spend and revenue by layer
  const layerAggregation: { [key in Layer]?: { spend: number; revenue: number } } = {};

  // Process spends by layer
  spends.forEach((spend) => {
    const layer = getRecordLayer(spend);
    if (!isNaN(spend.spend)) {
      if (!layerAggregation[layer]) {
        layerAggregation[layer] = { spend: 0, revenue: 0 };
      }
      layerAggregation[layer]!.spend += spend.spend;
    }
  });

  // Process revenues by layer
  revenues.forEach((revenue) => {
    const layer = getRecordLayer(revenue);
    if (!isNaN(revenue.revenue)) {
      if (!layerAggregation[layer]) {
        layerAggregation[layer] = { spend: 0, revenue: 0 };
      }
      layerAggregation[layer]!.revenue += revenue.revenue;
    }
  });

  // Handle Portfolio→Brand mapping when there's no actual Brand data
  const brandData = layerAggregation[Layer.Brand] || layerAggregation[Layer.Portfolio];

  // Build stages for layers with data
  const layersToCheck = [
    { data: brandData, label: FUNNEL_STAGE_LABELS[Layer.Brand] },
    { data: layerAggregation[Layer.Category], label: FUNNEL_STAGE_LABELS[Layer.Category] },
    { data: layerAggregation[Layer.Product], label: FUNNEL_STAGE_LABELS[Layer.Product] },
  ];

  const stages: FunnelStageData[] = layersToCheck
    .filter(({ data }) => data && (data.spend > 0 || data.revenue > 0))
    .map(({ data, label }) => ({
      stage: label,
      spend: data!.spend,
      revenue: data!.revenue,
      roi:
        data!.spend > 0 ? calculateROI({ spend: data!.spend, revenue: data!.revenue }) : undefined,
      spendPercentage: totalSpend > 0 ? data!.spend / totalSpend : 0,
      revenuePercentage: totalRevenue > 0 ? data!.revenue / totalRevenue : 0,
    }));

  return stages;
};
